import { Dialog } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Space, SpaceTag } from '@app/interfaces/space';
import { SpaceUserRole } from '@interfaces/space-access-control.types';
import { User } from '@interfaces/user';
import { NgbDropdownToggle, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '@services/user.service';
import { getTranslation } from '@shared/translate-helper';
@Component({
  selector: 'app-space-card',
  templateUrl: './space-card.component.html',
  styleUrls: ['./space-card.component.scss'],
  standalone: false,
})
export class SpaceCardComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() space!: Space;
  @Input() i!: number;
  @Input() fullTagList: SpaceTag[] = [];
  @Input() tagColorList: any[] = [];
  @Input() isLoading: boolean = false;

  @Output() spaceClick = new EventEmitter<string>();
  @Output() openUpdatePrompt = new EventEmitter<Space>();
  @Output() openDeletePrompt = new EventEmitter<Space>();
  @Output() openArchivePrompt = new EventEmitter<Space>();
  @Output() openUnarchivePrompt = new EventEmitter<Space>();
  @Output() openClonePrompt = new EventEmitter<Space>();
  @Output() addTagToSpace = new EventEmitter<{ tag: SpaceTag; space: Space }>();
  @Output() deleteTagFromSpace = new EventEmitter<{ tagId: string; space: Space }>();
  @Output() addTag = new EventEmitter<{ tagName: string; space: Space }>();
  @Output() updateTag = new EventEmitter<SpaceTag>();
  @Output() deleteTag = new EventEmitter<{ tagId: string; space: Space }>();
  @Output() tagsDropdownOpenChange = new EventEmitter<boolean>();

  public filterCreateTagName: string = '';
  public selectedTagId: string = '0';
  public selectedTagName: string = '';
  public selectedTagNameOriginal: string = '';
  public updateTagColor: string = '';
  public tagsToShow: number = 0;
  public hiddenTags: number = 0;
  public editPopover?: NgbPopover;
  public maxTagInputLength: number = 45;
  public errorText: string = '';
  public mySpaceRole: SpaceUserRole = 'unknown';

  @ViewChild(NgbDropdownToggle, { static: false }) dropdownToggle?: NgbDropdownToggle;
  @ViewChildren('tagElement', { read: ElementRef }) tagElements!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('tagsContainer', { read: ElementRef }) tagsContainer!: ElementRef<HTMLElement>;
  @ViewChild('filterTagAutofocus', { read: ElementRef }) filterInput?: ElementRef<HTMLInputElement>;
  @ViewChild('deletePrompt') deletePrompt!: TemplateRef<any>;

  constructor(
    private dialog: Dialog,
    private userService: UserService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    window.addEventListener('resize', this.onWindowResize);
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  onWindowResize = () => {
    // Use setTimeout to wait for view to stabilize if needed
    setTimeout(() => {
      this.updateTagsToShowHide();
    }, 100);
  };

  ngAfterViewInit() {
    // Use setTimeout to wait for view to stabilize if needed
    setTimeout(() => {
      this.updateTagsToShowHide();
      // Order: owner (unique) -> current user (if not owner) -> rest ordered by last name, then first name
      if (Array.isArray(this.space.users)) {
        const currentUser = this.userService.getUserData();
        const currentUserUuid = currentUser?.uuid;

        const normalize = (value: string | undefined | null) =>
          (value || '')
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase();
        const sortKey = (u: any) => normalize(`${u.first_name || ''} ${u.last_name || ''}`);

        const owner = this.space.users.find((u) => u.role === 'owner') || null;
        const isCurrentOwner = !!owner && owner.user_uuid === currentUserUuid;

        this.mySpaceRole = this.getMyRoleInSpace(currentUser!);

        const withoutOwner = owner
          ? this.space.users.filter((u) => u.user_uuid !== owner.user_uuid)
          : [...this.space.users];

        const currentUserEntry = withoutOwner.find((u) => u.user_uuid === currentUserUuid) || null;
        let rest = withoutOwner.filter((u) => u.user_uuid !== currentUserUuid);
        rest = rest.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

        if (owner && isCurrentOwner) {
          this.space.users = [owner, ...rest];
        } else if (owner && currentUserEntry) {
          this.space.users = [owner, currentUserEntry, ...rest];
        } else if (owner) {
          this.space.users = [owner, ...rest];
        } else if (currentUserEntry) {
          this.space.users = [currentUserEntry, ...rest];
        } else {
          this.space.users = rest;
        }
      }
    }, 200);
  }

  private getMyRoleInSpace(currentUser: User | null): SpaceUserRole {
    if (!this.space || !currentUser) return 'unknown';

    const userUuid = currentUser?.uuid;

    // Search for the user in the space's user list
    const matchedUser = this.space.users?.find((spaceUser) => spaceUser.user_uuid === userUuid);

    if (matchedUser) return matchedUser.role || 'unknown';

    // If not found, return "unknown" or handle it as needed
    return 'unknown';
  }

  updateTagsToShowHide() {
    let totalWidth = 0;
    this.tagsToShow = 0;
    this.hiddenTags = 0;
    this.tagElements.forEach((elRef: ElementRef<HTMLElement>) => {
      if (elRef && elRef.nativeElement) {
        const style = getComputedStyle(elRef.nativeElement);
        const marginLeft = parseFloat(style.marginLeft);
        const marginRight = parseFloat(style.marginRight);
        const width = elRef.nativeElement.offsetWidth;
        totalWidth += marginLeft + marginRight + width;
        const el = this.tagsContainer.nativeElement;
        const maxWidth = window.getComputedStyle(el).maxWidth;
        const resolvedMaxWidth = this.resolveCalcValue(el, maxWidth);
        if (totalWidth < resolvedMaxWidth) this.tagsToShow++;
        else this.hiddenTags++;
      }
    });
  }

  resolveCalcValue(element: HTMLElement, calcValue: string): number {
    if (!calcValue.includes('calc')) return parseInt(calcValue);
    const inner = calcValue.slice(5, -1).trim();
    const parentWidth = element.parentElement?.getBoundingClientRect().width || 0;
    const replaced = inner.replace(/([\d.]+)%/g, (_, p1) => ((parseFloat(p1) / 100) * parentWidth).toString());
    const expression = replaced.replace(/px/g, '');
    const result = Function(`"use strict"; return (${expression})`)();
    return Math.floor(result);
  }

  gotoSpaceDetails(uuid: string): void {
    this.spaceClick.emit(uuid);
  }

  onOpenUpdatePrompt(space: Space): void {
    this.openUpdatePrompt.emit(space);
  }

  onOpenDeletePrompt(space: Space): void {
    this.openDeletePrompt.emit(space);
  }

  onOpenArchivePrompt(space: Space): void {
    this.openArchivePrompt.emit(space);
  }

  onOpenUnarchivePrompt(space: Space, event: Event): void {
    this.openUnarchivePrompt.emit(space);
    event.stopPropagation();
  }

  onCloneSpace(space: Space): void {
    this.openClonePrompt.emit(space);
  }

  onAddTagToSpace(tag: SpaceTag, space: Space): void {
    this.filterCreateTagName = '';
    this.addTagToSpace.emit({ tag, space });
    setTimeout(() => {
      this.updateTagsToShowHide();
    }, 100);
  }

  onDeleteTagFromSpace(tagId: string, space: Space): void {
    this.deleteTagFromSpace.emit({ tagId, space });
    setTimeout(() => {
      this.updateTagsToShowHide();
    }, 100);
  }

  onAddTag(name: string): void {
    this.addTag.emit({ tagName: name, space: this.space });
    this.filterCreateTagName = '';
    setTimeout(() => {
      this.updateTagsToShowHide();
    }, 100);
  }

  onUpdateTag(tagObj: SpaceTag): void {
    if (this.editPopover && this.editPopover.isOpen()) {
      this.editPopover.close();
    }
    this.updateTag.emit(tagObj);
  }

  onDeleteTag(): void {
    if (this.editPopover && this.editPopover.isOpen()) {
      this.editPopover.close();
    }
    this.deleteTag.emit({ tagId: this.selectedTagId, space: this.space });
  }

  getNonAddedTagList() {
    return this.fullTagList
      .filter((tag) => !this.space.tags?.some((spaceTag) => spaceTag.uuid === tag.uuid))
      .filter((tag) => tag.name.toLowerCase().includes(this.filterCreateTagName.toLowerCase()));
  }

  getShowAddButton(): boolean {
    return !this.fullTagList.some((tag) => tag.name.toLowerCase() === this.filterCreateTagName.toLowerCase());
  }

  editSelectedTag(p: NgbPopover, tag: SpaceTag) {
    const popovers = document.querySelectorAll('ngb-popover-window');
    popovers.forEach((popover: any) => {
      if (popover && popover.parentElement) {
        popover.parentElement.removeChild(popover);
      }
    });
    this.editPopover = p;
    this.selectedTagName = tag.name;
    this.selectedTagNameOriginal = tag.name;
    this.selectedTagId = tag.uuid;
    this.updateTagColor = tag.color;
    p.open();
  }

  onTagsDropdownOpen(isOpen: boolean) {
    if (isOpen) {
      setTimeout(() => {
        const input = this.filterInput?.nativeElement;
        if (input) {
          input.focus();
          input.select();
        }
        this.tagsDropdownOpenChange.emit(isOpen);
      }, 100);
    } else {
      this.tagsDropdownOpenChange.emit(isOpen);
    }
  }

  onFilterInputResize(event: Event): void {
    const inputEl = event.target as HTMLInputElement | null;
    if (!inputEl) return;
    const min = 50;
    const max = 200;
    const computed = Math.min(Math.max(min, inputEl.value.length * 10), max);
    inputEl.style.width = `${computed}px`;
  }

  openDeleteConfirmation() {
    this.dialog.open(this.deletePrompt, {
      panelClass: ['md_dialog_wrapper', 'med_modal-dialog'],
    });
  }
  closeDialog(): void {
    this.dialog.closeAll();
  }

  isTextTruncated(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth;
  }

  getTooltipText(tagName: string, element: HTMLElement): string | null {
    return this.isTextTruncated(element) ? tagName : null;
  }

  addTagByEnter() {
    if (this.filterCreateTagName) {
      const index = this.getNonAddedTagList().findIndex(
        (tag) => tag.name.toLocaleLowerCase() === this.filterCreateTagName.toLocaleLowerCase(),
      );
      if (index !== -1) {
        this.onAddTagToSpace(this.getNonAddedTagList()[index], this.space);
      } else {
        const ind = this.fullTagList.findIndex(
          (tag) => tag.name.toLocaleLowerCase() === this.filterCreateTagName.toLocaleLowerCase(),
        );
        if (ind === -1) this.onAddTag(this.filterCreateTagName);
      }
      setTimeout(() => {
        const input = this.filterInput?.nativeElement;
        if (input) {
          input.focus();
        }
      }, 100);
    }
  }

  checkTagInputLength(event: KeyboardEvent): void {
    const inputEl = event.target as HTMLInputElement | null;
    if (!inputEl) return;
    if (this.errorText && this.errorText.length <= this.maxTagInputLength) {
      this.errorText = '';
    }
    if (
      inputEl.value.length >= this.maxTagInputLength &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      !event.ctrlKey
    ) {
      getTranslation(this.translate, 'space_tag_max_length_error', { maxLength: this.maxTagInputLength }).subscribe(
        (t: any) => {
          this.errorText = t;
        },
      );
      event.preventDefault();
    }
  }
}
